var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import debounce from "debounce-promise";
import { defaultPageSize } from "healthex-common/constants";
import { WebhookConfigurationStatus, WebhookDeliveryStatus, } from "healthex-common/webhook.dtos";
import { computed, observable } from "mobx";
import { Disposer } from "../Infra/Disposer";
import { NOT_LOADED } from "../Infra/RemoteData";
const MAX_ATTEMPTS = 8;
export class WebhookDeliveryLogViewModel {
    constructor(opts) {
        this.disposer = new Disposer();
        this.filter = "ALL";
        this.searchText = "";
        this.selectedAttemptRow = null;
        this.paginationInput = { page: 0, pageSize: defaultPageSize };
        this.total = 0;
        this.webhook = NOT_LOADED;
        this.logs = NOT_LOADED;
        this.loadDeliveryLogsDebounced = debounce(() => this.loadDeliveryLogs(), 500);
        this.bannerUIManager = opts.container.bannerUIManager;
        this.routingUIManager = opts.container.routingUIManager;
        this.provisioningService = opts.container.provisioningService;
        this.authManager = opts.container.authManager;
    }
    run() {
        this.disposer.reaction(() => [this.webhookId, this.filter], () => void this.loadDeliveryLogs(), { fireImmediately: true, name: "loadDeliveryLogs" });
        this.disposer.reaction(() => this.searchText, () => void this.loadDeliveryLogsDebounced(), { fireImmediately: false, name: "loadDeliveryLogsDebounced" });
    }
    stop() {
        this.disposer.dispose();
    }
    updateWebhook(webhook) {
        this.webhook = webhook;
    }
    get webhookId() {
        var _a, _b;
        return (_b = (_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.webhookId) !== null && _b !== void 0 ? _b : null;
    }
    get attempts() {
        if (this.logs === NOT_LOADED)
            return NOT_LOADED;
        return this.logs.map((log) => ({
            id: log.id,
            messageId: log.messageId,
            timestampLabel: log.timestamp.toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
            }),
            event: log.eventType,
            projectId: log.project.id,
            projectName: log.project.title,
            statusLabel: log.responseSummary,
            outcome: log.success ? "DELIVERED" : log.willRetry ? "RETRIED" : "FAILED",
            attempt: log.attempt,
            maxAttempts: MAX_ATTEMPTS,
            eventHeadersFormatted: JSON.stringify(log.eventHeaders, null, 2),
            eventDataFormatted: JSON.stringify(log.eventData, null, 2),
            responseLatencyMs: log.responseTimeMs,
            responseSizeBytes: log.responseBody !== null ? new TextEncoder().encode(log.responseBody).length : null,
            responseBody: log.responseBody,
        }));
    }
    get noRowsMessage() {
        if (this.filter !== "ALL" || this.searchText.trim().length > 0) {
            return "No delivery logs match your filters";
        }
        return "No delivery logs found";
    }
    get selectedInspector() {
        return this.selectedAttemptRow;
    }
    get replayEnabled() {
        if (this.webhook === NOT_LOADED)
            return false;
        return this.webhook.status === WebhookConfigurationStatus.ACTIVE;
    }
    async loadDeliveryLogs() {
        if (this.authManager.orgId === null || this.webhookId === null)
            return;
        this.logs = NOT_LOADED;
        const start = this.paginationInput.page * this.paginationInput.pageSize;
        const end = start + this.paginationInput.pageSize;
        const result = await this.provisioningService.getWebhookDeliveryLogs(this.authManager.orgId, this.webhookId, {
            text: this.searchText.trim().length > 0 ? this.searchText.trim() : undefined,
            status: this.filter === "ALL"
                ? null
                : this.filter === "DELIVERED"
                    ? WebhookDeliveryStatus.DELIVERED
                    : this.filter === "RETRIED"
                        ? WebhookDeliveryStatus.RETRIED
                        : WebhookDeliveryStatus.FAILED,
        }, start, end);
        this.logs = result.results;
        this.total = result.total;
    }
    refresh() {
        void this.loadDeliveryLogs();
    }
    setFilter(filter) {
        this.filter = filter;
        this.paginationInput = { ...this.paginationInput, page: 0 };
    }
    setSearchText(searchText) {
        this.searchText = searchText;
        this.paginationInput = { ...this.paginationInput, page: 0 };
    }
    updatePagination(paginationParams) {
        this.paginationInput = paginationParams;
        void this.loadDeliveryLogs();
    }
    followMessage() {
        var _a;
        const messageId = (_a = this.selectedInspector) === null || _a === void 0 ? void 0 : _a.messageId;
        if (!messageId)
            return;
        this.setSearchText(messageId);
    }
    selectAttempt(attempt) {
        this.selectedAttemptRow = attempt;
    }
    closeInspector() {
        this.selectedAttemptRow = null;
    }
    async replayDelivery() {
        const row = this.selectedInspector;
        if (!row || !this.authManager.orgId)
            return;
        try {
            await this.provisioningService.requeueWebhookDeliveryLog(this.authManager.orgId, row.id);
            this.bannerUIManager.displayBanner({ text: `Replayed delivery for ${row.messageId}` });
        }
        catch (error) {
            this.bannerUIManager.displayBanner({ text: `Failed to replay delivery for ${row.messageId}`, type: "ERROR" });
        }
    }
    async copyMessageId() {
        var _a;
        const messageId = (_a = this.selectedInspector) === null || _a === void 0 ? void 0 : _a.messageId;
        if (!messageId)
            return;
        await navigator.clipboard.writeText(messageId);
        this.bannerUIManager.displayBanner({ text: "Message ID copied to clipboard" });
    }
}
__decorate([
    observable
], WebhookDeliveryLogViewModel.prototype, "filter", void 0);
__decorate([
    observable
], WebhookDeliveryLogViewModel.prototype, "searchText", void 0);
__decorate([
    observable
], WebhookDeliveryLogViewModel.prototype, "selectedAttemptRow", void 0);
__decorate([
    observable
], WebhookDeliveryLogViewModel.prototype, "paginationInput", void 0);
__decorate([
    observable
], WebhookDeliveryLogViewModel.prototype, "total", void 0);
__decorate([
    observable
], WebhookDeliveryLogViewModel.prototype, "webhook", void 0);
__decorate([
    observable
], WebhookDeliveryLogViewModel.prototype, "logs", void 0);
__decorate([
    computed
], WebhookDeliveryLogViewModel.prototype, "webhookId", null);
__decorate([
    computed
], WebhookDeliveryLogViewModel.prototype, "attempts", null);
__decorate([
    computed
], WebhookDeliveryLogViewModel.prototype, "noRowsMessage", null);
__decorate([
    computed
], WebhookDeliveryLogViewModel.prototype, "selectedInspector", null);
__decorate([
    computed
], WebhookDeliveryLogViewModel.prototype, "replayEnabled", null);
